// src\components\admin\ProjectModal.jsx
// SEO/OG fields are stored in the database - server uses them for OG generation

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiX,
  FiSave,
  FiPlus,
  FiTag,
  FiHeart,
  FiEye,
  FiAlertCircle,
  FiGrid,
  FiImage,
  FiShare2,
  FiTrendingUp,
  FiLink,
  FiCopy,
  FiZap,
  FiInfo,
  FiPenTool,
  FiFolder,
  FiEdit3,
  FiCheck,
} from 'react-icons/fi';
import { TfiLightBulb } from 'react-icons/tfi';
import { useTheme } from '../../contexts/ThemeContext';
import ImageUploader from './ImageUploader';
import { TagManager } from '../admin/blog/TagManager';

// =============================================
// THEME CONFIG — per workType
// =============================================
const THEME = {
  portfolio: {
    accentColor: '#3b82f6',
    accentRgb: '59, 130, 246',
    gradientFrom: 'from-blue-500/10',
    gradientTo: 'to-indigo-500/10',
    ringColor: 'focus:ring-blue-500',
    icon: FiFolder,
    label: 'Project',
    labelPlural: 'Projects',
  },
  illustration: {
    accentColor: '#ec4899',
    accentRgb: '236, 72, 153',
    gradientFrom: 'from-pink-500/10',
    gradientTo: 'to-orange-500/10',
    ringColor: 'focus:ring-pink-500',
    icon: FiPenTool,
    label: 'Illustration',
    labelPlural: 'Illustrations',
  },
};

// =============================================
// SPECIAL VALUE: Custom category trigger
// =============================================
const CUSTOM_CATEGORY_VALUE = '__custom__';

// =============================================
// SOCIAL PREVIEW INDICATOR
// =============================================
const SocialPreviewIndicator = ({ ogImage, ogTitle, ogDescription }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  if (!ogImage && !ogTitle && !ogDescription) return null;

  return (
    <div
      className="mt-2 p-3 rounded-xl border"
      style={{
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <FiShare2 className="w-3.5 h-3.5 text-blue-500" />
        <span
          className="text-[10px] font-medium"
          style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
        >
          Social Sharing Preview
        </span>
        <span
          className="text-[9px]"
          style={{
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          }}
        >
          (OG tags generated server-side)
        </span>
      </div>
      <div
        className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800"
        style={{ aspectRatio: '1.91/1' }}
      >
        {ogImage ? (
          <>
            <img
              src={ogImage}
              alt="OG Preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
              {ogTitle && (
                <p className="text-white font-semibold text-sm line-clamp-1">
                  {ogTitle}
                </p>
              )}
              {ogDescription && (
                <p className="text-white/70 text-xs line-clamp-1 mt-0.5">
                  {ogDescription}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FiImage className="w-8 h-8 mx-auto opacity-30" />
              <p
                className="text-xs mt-2 opacity-50"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                No OG image set
              </p>
              <p
                className="text-[10px] opacity-30"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                Set a thumbnail image to auto-generate
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
function ProjectModal({
  isOpen,
  onClose,
  project,
  onSave,
  onUploadImage,
  onDeleteImage,
  onDeleteTempImage,
  isEditing,
  saving,
  projectId = null,
  defaultWorkType = 'portfolio',
}) {
  const { actualTheme } = useTheme();
  const modalRef = useRef(null);
  const textareaRef = useRef(null);
  const customCategoryInputRef = useRef(null);

  // =============================================
  // INITIAL FORM STATE
  // =============================================
  const getInitialState = useCallback(
    wt => ({
      title: '',
      description: '',
      category: wt === 'illustration' ? 'logo-recreation' : 'branding',
      workType: wt,
      type: 'project',
      client: '',
      tags: [],
      featured: false,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
    }),
    [],
  );

  const [formData, setFormData] = useState(() =>
    getInitialState(defaultWorkType),
  );
  const [imageItems, setImageItems] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(-1);
  const [parseError, setParseError] = useState('');
  const [errors, setErrors] = useState({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingImageId, setUploadingImageId] = useState(null);

  // ✅ Custom category state
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategoryValue, setCustomCategoryValue] = useState('');

  // Creative suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  // =============================================
  // CATEGORIES — per workType
  // =============================================

  // ---------- Portfolio categories (unchanged) ----------
  const portfolioCategories = [
    {
      value: 'branding',
      label: 'Brand Design',
      icon: '🎨',
      description: 'Logo, identity, brand guidelines',
    },
    {
      value: 'product',
      label: 'Product Visual',
      icon: '📱',
      description: 'UI/UX, product design, mockups',
    },
    {
      value: 'others',
      label: 'Other Works',
      icon: '✨',
      description: 'Illustrations, animations, etc.',
    },
  ];

  // ---------- Illustration categories (Image Tracing services) ----------
  const illustrationCategories = [
    {
      value: 'logo-recreation',
      label: 'Logo Recreation',
      icon: '🔄',
      description: 'Convert raster logos into clean scalable vectors',
    },
    {
      value: 'vector-tracing',
      label: 'Vector Tracing',
      icon: '✒️',
      description: 'Trace raster artwork into precise vector paths',
    },
    {
      value: 'clipart-tracing',
      label: 'Clipart Tracing',
      icon: '🎭',
      description: 'Turn clipart and graphics into editable vectors',
    },
    {
      value: 'photo-to-vector',
      label: 'Photo to Vector',
      icon: '📸',
      description: 'Convert photos into stylized vector illustrations',
    },
    {
      value: 'line-art',
      label: 'Line Art',
      icon: '✏️',
      description: 'Clean, minimal line-art tracing',
    },
    {
      value: 'portrait-tracing',
      label: 'Portrait Tracing',
      icon: '👤',
      description: 'Vectorize portraits with clean, editable paths',
    },
    {
      value: 'sticker-design',
      label: 'Sticker Design',
      icon: '🏷️',
      description: 'Die-cut ready vector stickers with clean edges',
    },
    {
      value: 'icon-vectorization',
      label: 'Icon Vectorization',
      icon: '⬢',
      description: 'Redraw icons as crisp, scalable SVGs',
    },
    {
      value: 'signature-tracing',
      label: 'Signature Tracing',
      icon: '✍️',
      description: 'Convert handwritten signatures into clean vectors',
    },
    {
      value: 'silhouette-tracing',
      label: 'Silhouette Tracing',
      icon: '🌓',
      description: 'Clean silhouettes from photos or artwork',
    },
    {
      value: 'pattern-tracing',
      label: 'Pattern Tracing',
      icon: '🧩',
      description: 'Repeating pattern extraction and vector rebuild',
    },
    {
      value: 'sports-logo-tracing',
      label: 'Sports & Team Logos',
      icon: '🏆',
      description: 'Sports emblems, mascots, team logos in vector',
    },
    {
      value: 'others',
      label: 'Other Works',
      icon: '✨',
      description: 'Other illustration types',
    },
  ];

  const categories =
    formData.workType === 'illustration'
      ? illustrationCategories
      : portfolioCategories;

  const workTypes = [
    {
      value: 'portfolio',
      label: 'Portfolio',
      icon: '📁',
      description: 'Main portfolio work',
    },
    {
      value: 'illustration',
      label: 'Illustration',
      icon: '🎨',
      description: 'Illustration collection',
    },
  ];

  const projectTypes = [
    {
      value: 'project',
      label: 'Project',
      icon: '📁',
      description: 'Client projects and professional work',
    },
    {
      value: 'random',
      label: 'Random',
      icon: '🎲',
      description: 'Personal experiments and creative exploration',
    },
  ];

  // =============================================
  // TAG SUGGESTIONS
  // =============================================
  const generateTagSuggestions = () => {
    setSuggestionLoading(true);
    const suggestions = new Set();

    if (formData.title) {
      const titleWords = formData.title.toLowerCase().split(' ');
      titleWords.forEach(word => {
        if (word.length > 3) suggestions.add(word);
      });
    }

    if (formData.description) {
      const descWords = formData.description.toLowerCase().split(' ');
      const commonWords = [
        'the',
        'be',
        'to',
        'of',
        'and',
        'a',
        'in',
        'that',
        'have',
        'i',
        'it',
        'for',
        'not',
        'on',
        'with',
        'he',
        'as',
        'you',
        'do',
        'at',
        'this',
        'but',
        'his',
        'by',
        'from',
        'they',
        'we',
        'say',
        'her',
        'she',
        'or',
        'an',
        'will',
        'my',
        'one',
        'all',
        'would',
        'there',
        'their',
      ];

      descWords.forEach(word => {
        const cleanWord = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
        if (cleanWord.length > 3 && !commonWords.includes(cleanWord)) {
          suggestions.add(cleanWord);
        }
      });
    }

    // ✅ Updated category map with new illustration categories
    const categoryMap = {
      branding: [
        'branding',
        'identity',
        'logo',
        'visual-identity',
        'brand-guidelines',
      ],
      product: ['product', 'ui-ux', 'design', 'mockup', 'prototype'],
      others: ['creative', 'art', 'illustration', 'animation', 'experimental'],
      // Illustration / image tracing
      'logo-recreation': ['logo', 'recreation', 'rebrand', 'vector-logo'],
      'vector-tracing': ['vector', 'tracing', 'svg', 'illustrator'],
      'clipart-tracing': ['clipart', 'graphic', 'vectorize'],
      'photo-to-vector': ['photo', 'vector-art', 'stylized', 'photo-to-vector'],
      'line-art': ['line-art', 'linework', 'outline', 'minimal'],
      'portrait-tracing': ['portrait', 'face', 'figure', 'vector-portrait'],
      'sticker-design': ['sticker', 'die-cut', 'vector-sticker'],
      'icon-vectorization': ['icon', 'svg', 'vector-icon', 'ui-icons'],
      'signature-tracing': ['signature', 'handwritten', 'autograph'],
      'silhouette-tracing': ['silhouette', 'outline', 'shape'],
      'pattern-tracing': ['pattern', 'seamless', 'repeating'],
      'sports-logo-tracing': ['sports', 'team-logo', 'emblem', 'mascot'],
    };

    const categoryTags = categoryMap[formData.category] || [
      'design',
      'creative',
    ];
    categoryTags.forEach(tag => suggestions.add(tag));

    if (formData.workType === 'illustration') {
      [
        'illustration',
        'image-tracing',
        'digital-art',
        'vector',
        'drawing',
      ].forEach(tag => suggestions.add(tag));
    } else {
      suggestions.add('portfolio');
    }

    if (formData.type === 'project') {
      suggestions.add('professional');
    } else {
      suggestions.add('personal');
      suggestions.add('experimental');
    }

    const existingTags = new Set(formData.tags);
    const finalSuggestions = Array.from(suggestions)
      .filter(tag => !existingTags.has(tag))
      .slice(0, 8);

    setSuggestedTags(finalSuggestions);
    setSuggestionLoading(false);
    setShowSuggestions(true);

    setTimeout(() => {
      if (!showSuggestions) return;
      setShowSuggestions(false);
    }, 8000);
  };

  useEffect(() => {
    if (formData.title && formData.description) {
      const timer = setTimeout(() => {
        generateTagSuggestions();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.title,
    formData.description,
    formData.category,
    formData.type,
    formData.workType,
  ]);

  // =============================================
  // THUMBNAIL HELPER
  // =============================================
  const getThumbnailUrl = () => {
    if (imageItems.length === 0) return '';
    const thumbIndex =
      thumbnailIndex >= 0 && thumbnailIndex < imageItems.length
        ? thumbnailIndex
        : 0;
    const thumb = imageItems[thumbIndex];
    return thumb?.uploadedUrl || thumb?.url || '';
  };

  // =============================================
  // AUTO SEO/OG GENERATION
  // =============================================
  const autoGenerateSEOFields = useCallback(() => {
    const title = formData.title || '';
    const description = formData.description || '';
    const tags = formData.tags || [];
    const thumbnailImage = getThumbnailUrl();

    const updates = {};

    if (!formData.seoTitle && title) {
      updates.seoTitle =
        title.length > 60
          ? title.substring(0, 57) + '... - WD Hejbul'
          : title + ' - WD Hejbul';
    }

    if (!formData.seoDescription && description) {
      updates.seoDescription =
        description.length > 160
          ? description.substring(0, 157) + '...'
          : description;
    }

    if (!formData.ogTitle && title) {
      updates.ogTitle =
        title.length > 90 ? title.substring(0, 87) + '...' : title;
    }

    if (!formData.ogDescription && description) {
      updates.ogDescription =
        description.length > 200
          ? description.substring(0, 197) + '...'
          : description;
    }

    if (!formData.ogImage && thumbnailImage) {
      updates.ogImage = thumbnailImage;
    }

    if (!formData.twitterTitle && (updates.ogTitle || formData.ogTitle)) {
      updates.twitterTitle = updates.ogTitle || formData.ogTitle;
    }
    if (
      !formData.twitterDescription &&
      (updates.ogDescription || formData.ogDescription)
    ) {
      updates.twitterDescription =
        updates.ogDescription || formData.ogDescription;
    }
    if (!formData.twitterImage && (updates.ogImage || formData.ogImage)) {
      updates.twitterImage = updates.ogImage || formData.ogImage;
    }

    if (
      (!formData.seoKeywords || formData.seoKeywords.length === 0) &&
      tags.length > 0
    ) {
      updates.seoKeywords = tags.slice(0, 10);
    }

    setFormData(prev => ({ ...prev, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // =============================================
  // FORM RESET / LOAD
  // =============================================
  useEffect(() => {
    if (project) {
      // ============ EDIT MODE ============
      const storedCategory = project.category || 'branding';
      const storedWorkType = project.workType || 'portfolio';

      // Check if stored category is in the known list
      const knownValues =
        storedWorkType === 'illustration'
          ? illustrationCategories.map(c => c.value)
          : portfolioCategories.map(c => c.value);

      const isCustom = !knownValues.includes(storedCategory);

      setFormData({
        title: project.title || '',
        description: project.description || '',
        category: storedCategory,
        type: project.type || 'project',
        workType: storedWorkType,
        client: project.type === 'random' ? '' : project.client || '',
        tags: project.tags || [],
        featured: project.featured || false,
        seoTitle: project.seoTitle || '',
        seoDescription: project.seoDescription || '',
        seoKeywords: project.seoKeywords || [],
        ogTitle: project.ogTitle || '',
        ogDescription: project.ogDescription || '',
        ogImage: project.ogImage || '',
        twitterTitle: project.twitterTitle || '',
        twitterDescription: project.twitterDescription || '',
        twitterImage: project.twitterImage || '',
      });

      // ✅ Handle custom category on edit
      if (isCustom) {
        setIsCustomCategory(true);
        setCustomCategoryValue(storedCategory);
      } else {
        setIsCustomCategory(false);
        setCustomCategoryValue('');
      }

      let images = [];
      if (project.images && project.images.length > 0) {
        images = project.images.map((img, idx) => ({
          id: img.publicId || `existing_${idx}`,
          url: img.url,
          status: 'uploaded',
          isExisting: true,
          order: idx,
          publicId: img.publicId,
          uploadedUrl: img.url,
          uploadProgress: 100,
        }));
        const thumbIdx = project.images.findIndex(
          img => img.url === project.thumbnailUrl,
        );
        setThumbnailIndex(thumbIdx >= 0 ? thumbIdx : 0);
      } else if (project.imageUrl) {
        images = [
          {
            id: 'existing_0',
            url: project.imageUrl,
            status: 'uploaded',
            isExisting: true,
            order: 0,
            uploadedUrl: project.imageUrl,
            uploadProgress: 100,
          },
        ];
        setThumbnailIndex(0);
      }
      setImageItems(images);
      setErrors({});
    } else {
      // ============ NEW MODE ============
      setFormData(getInitialState(defaultWorkType));
      setImageItems([]);
      setThumbnailIndex(-1);
      setErrors({});
      setIsCustomCategory(false);
      setCustomCategoryValue('');
    }
    setParseError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, isOpen, defaultWorkType, getInitialState]);

  // Reset client when type is random OR workType is illustration
  useEffect(() => {
    if (formData.type === 'random' || formData.workType === 'illustration') {
      setFormData(prev => (prev.client ? { ...prev, client: '' } : prev));
    }
  }, [formData.type, formData.workType]);

  // Auto-generate SEO when images or thumbnail changes
  useEffect(() => {
    if (imageItems.length > 0 && thumbnailIndex >= 0) {
      const timer = setTimeout(() => {
        autoGenerateSEOFields();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageItems, thumbnailIndex]);

  // Auto-focus custom category input when it appears
  useEffect(() => {
    if (isCustomCategory && customCategoryInputRef.current) {
      // Small delay so the DOM is ready
      setTimeout(() => {
        customCategoryInputRef.current?.focus();
      }, 50);
    }
  }, [isCustomCategory]);

  // ESC key closes modal
  useEffect(() => {
    const handleEsc = e => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Auto-expand textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + 'px';
    }
  }, [formData.description]);

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) return;
  };

  // =============================================
  // VALIDATION
  // =============================================
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (formData.title.length > 100)
      newErrors.title = 'Title must be less than 100 characters';

    if (!formData.description.trim())
      newErrors.description = 'Description is required';
    if (formData.description.length < 20)
      newErrors.description = 'Description must be at least 20 characters';

    if (!formData.workType) newErrors.workType = 'Work Type is required';

    // ✅ Custom category validation
    if (isCustomCategory) {
      const trimmed = customCategoryValue.trim();
      if (!trimmed) {
        newErrors.category = 'Please enter a custom category name';
      } else if (trimmed.length < 2) {
        newErrors.category = 'Custom category must be at least 2 characters';
      } else if (trimmed.length > 40) {
        newErrors.category = 'Custom category must be 40 characters or less';
      } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(trimmed)) {
        newErrors.category =
          'Only letters, numbers, spaces, hyphens, and underscores allowed';
      }
    } else if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (imageItems.length === 0)
      newErrors.image = 'At least one project image is required';

    const hasPending = imageItems.some(
      img => img.status === 'pending' || img.status === 'uploading',
    );
    if (hasPending) {
      newErrors.image =
        'Please upload all pending images before saving. Click the upload button on each pending image.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =============================================
  // IMAGE HANDLERS
  // =============================================
  const handleImagesChange = newImages => {
    setImageItems(newImages);
    setErrors(prev => ({ ...prev, image: null }));
  };

  const handleThumbnailChange = index => {
    setThumbnailIndex(index);
  };

  const handleUploadImage = async (file, tempId) => {
    setIsUploadingImage(true);
    setUploadingImageId(tempId);

    try {
      if (onUploadImage) {
        const result = await onUploadImage(file, tempId);
        return result;
      }
      throw new Error('Upload function not available');
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setIsUploadingImage(false);
      setUploadingImageId(null);
    }
  };

  const handleDeleteImage = async (imageIndexOrPublicId, isTemp = false) => {
    if (isTemp) {
      const publicId = imageIndexOrPublicId;
      try {
        if (onDeleteTempImage) {
          await onDeleteTempImage(publicId);
        }
        const newImages = imageItems.filter(img => img.publicId !== publicId);
        setImageItems(newImages);
        if (thumbnailIndex >= newImages.length) {
          setThumbnailIndex(newImages.length > 0 ? 0 : -1);
        }
        return;
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          image: 'Failed to delete image from Cloudinary',
        }));
        return;
      }
    }

    const imageIndex = imageIndexOrPublicId;
    const itemToDelete = imageItems[imageIndex];

    if (!itemToDelete) return;

    if (itemToDelete?.isExisting && projectId && onDeleteImage) {
      await onDeleteImage(projectId, imageIndex);
      const newImages = imageItems.filter((_, i) => i !== imageIndex);
      setImageItems(newImages);
      updateThumbnailAfterDelete(newImages, imageIndex);
      return;
    }

    if (
      itemToDelete?.publicId &&
      itemToDelete?.status === 'uploaded' &&
      !itemToDelete?.isExisting
    ) {
      try {
        if (onDeleteTempImage) {
          await onDeleteTempImage(itemToDelete.publicId);
        } else if (onDeleteImage) {
          await onDeleteImage(itemToDelete.publicId, true);
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          image: 'Failed to delete image from Cloudinary',
        }));
        return;
      }
    }

    const newImages = imageItems.filter((_, i) => i !== imageIndex);
    setImageItems(newImages);
    updateThumbnailAfterDelete(newImages, imageIndex);
  };

  const updateThumbnailAfterDelete = (newImages, deletedIndex) => {
    if (thumbnailIndex >= newImages.length) {
      setThumbnailIndex(newImages.length > 0 ? 0 : -1);
    } else if (thumbnailIndex === deletedIndex) {
      setThumbnailIndex(newImages.length > 0 ? 0 : -1);
    } else if (thumbnailIndex > deletedIndex) {
      setThumbnailIndex(thumbnailIndex - 1);
    }
  };

  // =============================================
  // TAGS
  // =============================================
  const handleTagsChange = newTags => {
    setFormData(prev => ({ ...prev, tags: newTags }));
    setErrors(prev => ({ ...prev, tags: null }));
  };

  const handleAddSuggestion = tag => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
  };

  const handleAddAllSuggestions = () => {
    const newTags = [...formData.tags];
    let addedCount = 0;

    suggestedTags.forEach(tag => {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      setFormData(prev => ({ ...prev, tags: newTags }));
      setShowSuggestions(false);
    }
  };

  // =============================================
  // ✅ CUSTOM CATEGORY HANDLERS
  // =============================================
  const handleCategorySelectChange = value => {
    if (value === CUSTOM_CATEGORY_VALUE) {
      // Switch to custom mode
      setIsCustomCategory(true);
      setCustomCategoryValue('');
      setErrors(prev => ({ ...prev, category: null }));
    } else {
      // Normal category
      setIsCustomCategory(false);
      setCustomCategoryValue('');
      setFormData(prev => ({ ...prev, category: value }));
      setErrors(prev => ({ ...prev, category: null }));
    }
  };

  const handleCustomCategoryChange = value => {
    setCustomCategoryValue(value);
    setErrors(prev => ({ ...prev, category: null }));

    // Slugify on the fly for storage — keep it human-readable but URL-safe
    const slugified = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-_]/g, '');

    setFormData(prev => ({
      ...prev,
      category: slugified,
    }));
  };

  const handleResetCustomCategory = () => {
    setIsCustomCategory(false);
    setCustomCategoryValue('');
    const defaultCat =
      formData.workType === 'illustration' ? 'logo-recreation' : 'branding';
    setFormData(prev => ({ ...prev, category: defaultCat }));
    setErrors(prev => ({ ...prev, category: null }));
  };

  // =============================================
  // WORKTYPE CHANGE
  // =============================================
  const handleWorkTypeChange = newWorkType => {
    const validCategories =
      newWorkType === 'illustration'
        ? illustrationCategories.map(c => c.value)
        : portfolioCategories.map(c => c.value);

    // Reset custom category when switching workType
    setIsCustomCategory(false);
    setCustomCategoryValue('');

    setFormData(prev => ({
      ...prev,
      workType: newWorkType,
      category: validCategories.includes(prev.category)
        ? prev.category
        : validCategories[0],
    }));
  };

  // =============================================
  // SUBMIT
  // =============================================
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    autoGenerateSEOFields();

    const uploadedImageUrls = imageItems
      .filter(img => img.status === 'uploaded' && img.uploadedUrl)
      .map(img => img.uploadedUrl);

    const pendingFiles = imageItems
      .filter(img => img.status === 'pending' && img.file)
      .map(img => img.file);

    // ✅ Ensure final category is set correctly for custom categories
    let finalCategory = formData.category;
    if (isCustomCategory) {
      finalCategory = customCategoryValue
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-_]/g, '');
    }

    const submitData = {
      ...formData,
      category: finalCategory,
      thumbnailIndex: thumbnailIndex >= 0 ? thumbnailIndex : 0,
    };

    let imageData;
    if (pendingFiles.length > 0) {
      imageData = pendingFiles;
    } else if (uploadedImageUrls.length > 0) {
      imageData = uploadedImageUrls;
    } else {
      imageData = [];
    }

    await onSave(submitData, imageData);
  };

  if (!isOpen) return null;

  const isDark = actualTheme === 'dark';
  const isIllustration = formData.workType === 'illustration';

  // =============================================
  // ACTIVE THEME
  // =============================================
  const theme = isIllustration ? THEME.illustration : THEME.portfolio;
  const ThemeIcon = theme.icon;

  const accentStyle = { color: theme.accentColor };
  const accentBgSoft = {
    backgroundColor: `rgba(${theme.accentRgb}, 0.1)`,
  };
  const accentBgSofter = {
    backgroundColor: `rgba(${theme.accentRgb}, 0.06)`,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleBackdropClick}
      />

      <div
        ref={modalRef}
        className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl transition-all duration-300 animate-fade-in-up bg-gradient-to-br ${theme.gradientFrom} ${theme.gradientTo}`}
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ============================================ */}
        {/* Header */}
        {/* ============================================ */}
        <div
          className="sticky top-0 z-10 flex justify-between items-center p-5 border-b transition-colors duration-300"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: `rgba(${theme.accentRgb}, 0.2)`,
            borderBottomWidth: '2px',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300"
              style={accentBgSoft}
            >
              <ThemeIcon className="w-5 h-5" style={accentStyle} />
            </div>
            <div>
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {isEditing ? 'Edit' : 'Add New'} {theme.label}
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                {isEditing
                  ? `Update your ${theme.label.toLowerCase()} details and images`
                  : isIllustration
                    ? 'Showcase your illustration with images and details'
                    : 'Showcase your creative project with multiple images'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Stats Bar (edit mode) */}
        {isEditing && project && (project.likes > 0 || project.views > 0) && (
          <div
            className="px-5 py-3 border-b flex flex-wrap items-center gap-4 text-sm"
            style={{
              backgroundColor: 'var(--border-light)',
              borderColor: 'var(--border)',
            }}
          >
            {project.likes > 0 && (
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full bg-red-100 dark:bg-red-900/20">
                  <FiHeart className="w-3 h-3 text-red-500" />
                </div>
                <span style={{ color: 'var(--text)' }}>
                  <strong>{project.likes}</strong> likes
                </span>
              </div>
            )}
            {project.views > 0 && (
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full bg-blue-100 dark:bg-blue-900/20">
                  <FiEye className="w-3 h-3 text-blue-500" />
                </div>
                <span style={{ color: 'var(--text)' }}>
                  <strong>{project.views.toLocaleString()}</strong> views
                </span>
              </div>
            )}
            {project.images && (
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full bg-green-100 dark:bg-green-900/20">
                  <FiGrid className="w-3 h-3 text-green-500" />
                </div>
                <span style={{ color: 'var(--text)' }}>
                  <strong>{project.images.length}</strong> images
                </span>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* Form */}
        {/* ============================================ */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              {theme.label} Images{' '}
              {!isEditing && <span className="text-red-500">*</span>}
            </label>
            <ImageUploader
              images={imageItems}
              onImagesChange={handleImagesChange}
              thumbnailIndex={thumbnailIndex}
              onThumbnailChange={handleThumbnailChange}
              onUploadImage={handleUploadImage}
              onDeleteImage={handleDeleteImage}
              onDeleteTempImage={onDeleteTempImage}
              isUploading={saving}
              disabled={saving}
              isEditing={isEditing}
              uploadingImageId={uploadingImageId}
            />
            {errors.image && (
              <p className="text-xs text-red-500 mt-2 flex items-start gap-1">
                <FiAlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{errors.image}</span>
              </p>
            )}
            <div
              className="text-xs mt-3 space-y-1"
              style={{ color: 'var(--muted)' }}
            >
              <p>• Upload each image individually</p>
              <p>• Click the checkmark to set the thumbnail</p>
              <p>• The thumbnail is used as the OG image for social sharing</p>
            </div>
          </div>

          {/* Title */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={e => {
                setFormData({ ...formData, title: e.target.value });
                setErrors(prev => ({ ...prev, title: null }));
              }}
              className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor} ${
                errors.title ? 'border-red-500' : ''
              }`}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${errors.title ? '#ef4444' : 'var(--border)'}`,
                color: 'var(--text)',
              }}
              placeholder={
                isIllustration
                  ? 'e.g., Logo Recreation for Client Brand'
                  : 'e.g., Brand Identity for Tech Startup'
              }
              maxLength="100"
            />
            <div className="flex justify-between mt-1">
              {errors.title && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" /> {errors.title}
                </p>
              )}
              <p className="text-xs ml-auto" style={{ color: 'var(--muted)' }}>
                {formData.title.length}/100
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={textareaRef}
              required
              rows="4"
              value={formData.description}
              onChange={e => {
                setFormData({ ...formData, description: e.target.value });
                setErrors(prev => ({ ...prev, description: null }));
              }}
              className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor} resize-y min-h-[100px] ${
                errors.description ? 'border-red-500' : ''
              }`}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${errors.description ? '#ef4444' : 'var(--border)'}`,
                color: 'var(--text)',
              }}
              placeholder={
                isIllustration
                  ? 'Describe the tracing work, source image, technique, deliverables...'
                  : 'Describe your project in detail...'
              }
            />
            {errors.description && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <FiAlertCircle className="w-3 h-3" /> {errors.description}
              </p>
            )}
          </div>

          {/* Work Type */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Work Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.workType}
              onChange={e => handleWorkTypeChange(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor}`}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${errors.workType ? '#ef4444' : 'var(--border)'}`,
                color: 'var(--text)',
              }}
            >
              {workTypes.map(wt => (
                <option key={wt.value} value={wt.value}>
                  {wt.icon} {wt.label} - {wt.description}
                </option>
              ))}
            </select>
            {errors.workType && (
              <p className="text-xs text-red-500 mt-1">{errors.workType}</p>
            )}
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              {isIllustration ? (
                <>
                  <span style={accentStyle}>●</span> This will appear on the{' '}
                  <strong>Illustrations</strong> page (/illustrations)
                </>
              ) : (
                <>
                  <span style={accentStyle}>●</span> This will appear on the{' '}
                  <strong>Portfolio</strong> page (/portfolio)
                </>
              )}
            </p>
          </div>

          {/* ============================================ */}
          {/* CATEGORY + TYPE */}
          {/* ============================================ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Category */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  className="block text-sm font-medium"
                  style={{ color: 'var(--text)' }}
                >
                  Category <span className="text-red-500">*</span>
                </label>
                {isCustomCategory && (
                  <button
                    type="button"
                    onClick={handleResetCustomCategory}
                    className="text-[10px] hover:underline"
                    style={{ color: 'var(--muted)' }}
                  >
                    ← Back to list
                  </button>
                )}
              </div>

              {/* Normal dropdown OR custom input */}
              {!isCustomCategory ? (
                <select
                  value={formData.category}
                  onChange={e => handleCategorySelectChange(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor}`}
                  style={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${errors.category ? '#ef4444' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label} - {cat.description}
                    </option>
                  ))}
                  {/* ✅ Custom category trigger */}
                  <option value={CUSTOM_CATEGORY_VALUE}>
                    ✏️ Custom Category - Add your own...
                  </option>
                </select>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <FiEdit3
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                      style={{ color: theme.accentColor }}
                    />
                    <input
                      ref={customCategoryInputRef}
                      type="text"
                      value={customCategoryValue}
                      onChange={e => handleCustomCategoryChange(e.target.value)}
                      placeholder="e.g., Vintage Badge Revival"
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor} ${
                        errors.category ? 'border-red-500' : ''
                      }`}
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${errors.category ? '#ef4444' : theme.accentColor}`,
                        color: 'var(--text)',
                      }}
                      maxLength="40"
                      autoFocus
                    />
                  </div>

                  {/* Live preview of the stored slug */}
                  {customCategoryValue.trim() && (
                    <div
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px]"
                      style={accentBgSofter}
                    >
                      <FiCheck
                        className="w-3 h-3 flex-shrink-0"
                        style={accentStyle}
                      />
                      <span style={{ color: 'var(--muted)' }}>Saved as:</span>
                      <code
                        className="font-mono font-medium"
                        style={{ color: theme.accentColor }}
                      >
                        {customCategoryValue
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9\-_]/g, '') || '...'}
                      </code>
                    </div>
                  )}

                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    {customCategoryValue.length}/40 · Letters, numbers, spaces,
                    hyphens, underscores
                  </p>
                </div>
              )}

              {errors.category && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.category}
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                {isIllustration ? 'Illustration Type' : 'Project Type'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={e => {
                  setFormData({ ...formData, type: e.target.value });
                }}
                className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor}`}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                {projectTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Client — portfolio only */}
          {formData.type !== 'random' && !isIllustration && (
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                Client Name (Optional)
              </label>
              <input
                type="text"
                value={formData.client}
                onChange={e =>
                  setFormData({ ...formData, client: e.target.value })
                }
                className={`w-full px-4 py-2.5 rounded-xl outline-none transition-all focus:ring-2 ${theme.ringColor}`}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
                placeholder="Client or company name"
              />
            </div>
          )}

          {/* Tags */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={generateTagSuggestions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border-light)',
                  color: 'var(--text)',
                }}
              >
                <TfiLightBulb className="w-3.5 h-3.5" />
                Generate Suggestions
              </button>
            </div>
            {showSuggestions && suggestedTags.length > 0 && (
              <div
                className="mb-3 p-3 rounded-xl border animate-fade-in-up"
                style={{
                  backgroundColor: 'var(--border-light)',
                  borderColor: 'var(--border)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TfiLightBulb className="w-4 h-4 text-yellow-500" />
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--text)' }}
                    >
                      Creative Suggestions
                    </span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>
                      Based on your {theme.label.toLowerCase()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddAllSuggestions}
                      className="text-xs px-2 py-1 rounded hover:opacity-80"
                      style={{
                        backgroundColor: 'var(--bg)',
                        color: 'var(--text)',
                      }}
                    >
                      Add All
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className="text-xs hover:opacity-80"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestionLoading ? (
                    <div className="flex items-center gap-2 py-1">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                      <span
                        className="text-xs"
                        style={{ color: 'var(--muted)' }}
                      >
                        Generating suggestions...
                      </span>
                    </div>
                  ) : (
                    suggestedTags.map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleAddSuggestion(tag)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all hover:opacity-80 group"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <FiTag className="w-3 h-3" />
                        {tag}
                        <FiPlus className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            <TagManager
              selectedTags={formData.tags}
              onTagsChange={handleTagsChange}
              existingTags={[]}
              maxTags={20}
              variant="compact"
              placeholder={`Add ${theme.label.toLowerCase()} tags...`}
            />
          </div>

          {/* Featured checkbox */}
          <div
            className="flex items-center gap-3 p-3 rounded-xl transition-colors duration-300"
            style={accentBgSofter}
          >
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={e =>
                setFormData({ ...formData, featured: e.target.checked })
              }
              className="w-4 h-4 rounded accent-yellow-500"
            />
            <label
              htmlFor="featured"
              className="text-sm cursor-pointer flex-1"
              style={{ color: 'var(--text)' }}
            >
              <span className="font-medium">
                Feature this {theme.label.toLowerCase()}
              </span>
              <p
                className="text-xs mt-0.5 opacity-70"
                style={{ color: 'var(--muted)' }}
              >
                {isIllustration
                  ? 'Featured illustrations appear at the top of the Illustrations page.'
                  : 'Featured projects appear at the top of your portfolio and on the homepage.'}
              </p>
            </label>
          </div>

          {/* ============================================ */}
          {/* SEO & OG */}
          {/* ============================================ */}
          <div
            className="border-t pt-4 space-y-4"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiTrendingUp className="w-4 h-4" style={accentStyle} />
                <h3
                  className="text-sm font-semibold"
                  style={{ color: 'var(--text)' }}
                >
                  SEO & Social Sharing
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  autoGenerateSEOFields();
                }}
                className="px-2.5 py-1 text-[10px] rounded-lg transition-all hover:opacity-80 flex items-center gap-1.5"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                }}
              >
                <FiZap className="w-3 h-3" />
                Auto-generate
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Title
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (50-60 chars)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.seoTitle}
                  onChange={e =>
                    setFormData({ ...formData, seoTitle: e.target.value })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="SEO Title"
                  maxLength="60"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Title (Facebook/LinkedIn)
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (60-90 chars)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.ogTitle}
                  onChange={e =>
                    setFormData({ ...formData, ogTitle: e.target.value })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="OG Title"
                  maxLength="95"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Description
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (150-160 chars)
                  </span>
                </label>
                <textarea
                  value={formData.seoDescription}
                  onChange={e =>
                    setFormData({ ...formData, seoDescription: e.target.value })
                  }
                  rows="2"
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="SEO Description"
                  maxLength="160"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Description
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (150-200 chars)
                  </span>
                </label>
                <textarea
                  value={formData.ogDescription}
                  onChange={e =>
                    setFormData({ ...formData, ogDescription: e.target.value })
                  }
                  rows="2"
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="OG Description"
                  maxLength="200"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  OG Image URL
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from thumbnail)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.ogImage}
                  onChange={e =>
                    setFormData({ ...formData, ogImage: e.target.value })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="https://res.cloudinary.com/..."
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Twitter Image URL
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from OG)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.twitterImage}
                  onChange={e =>
                    setFormData({ ...formData, twitterImage: e.target.value })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="https://res.cloudinary.com/..."
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  SEO Keywords
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (5-10 keywords)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.seoKeywords.join(', ')}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      seoKeywords: e.target.value
                        .split(',')
                        .map(k => k.trim())
                        .filter(k => k),
                    })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>

              <div>
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Twitter Title
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from OG)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.twitterTitle}
                  onChange={e =>
                    setFormData({ ...formData, twitterTitle: e.target.value })
                  }
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="Twitter Title"
                />
              </div>

              <div className="md:col-span-2">
                <label
                  className="block text-[10px] font-medium mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Twitter Description
                  <span
                    className="ml-1 text-[9px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    (Auto-filled from OG)
                  </span>
                </label>
                <textarea
                  value={formData.twitterDescription}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      twitterDescription: e.target.value,
                    })
                  }
                  rows="2"
                  className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="Twitter Description"
                  maxLength="200"
                />
              </div>
            </div>

            <SocialPreviewIndicator
              ogImage={formData.ogImage}
              ogTitle={formData.ogTitle}
              ogDescription={formData.ogDescription}
            />

            <div
              className="p-2 rounded-lg text-[10px]"
              style={{
                backgroundColor: isDark
                  ? `rgba(${theme.accentRgb}, 0.08)`
                  : `rgba(${theme.accentRgb}, 0.04)`,
                border: `1px solid rgba(${theme.accentRgb}, 0.15)`,
              }}
            >
              <div className="flex items-center gap-2">
                <FiInfo className="w-3 h-3 flex-shrink-0" style={accentStyle} />
                <span
                  style={{
                    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
                  }}
                >
                  These fields are stored in the database and used by the server
                  to generate OG tags when your {theme.label.toLowerCase()} is
                  shared on social media.
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className="flex gap-3 pt-4 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all hover:opacity-90 disabled:opacity-50"
              style={{
                backgroundColor: theme.accentColor,
                color: '#ffffff',
              }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  {isEditing
                    ? `Update ${theme.label}`
                    : `Publish ${theme.label}`}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-2.5 rounded-xl font-medium transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectModal;
